'use client';

import { styled } from 'styled-components';

import { blogTokens } from '@dvd/blog/tokens';
import { sidebarCalloutFloatXl } from '@dvd/blog/tokens/layout-styles';
import BlogSubscribeForm, {
  RECAPTCHA_ENABLED,
} from '@dvd/blog/components/BlogSubscribeForm';
import Link from 'next/link';

type SubscribeProps = {
  /**
   * Variant option.
   *
   * - `sidebar` - Sidebar variant. Will appear in the right sidebar on the blog post page.
   * - `fullBody` - Full body variant. Will appear as a full-width block at the bottom of the blog post page.
   */
  variant?: 'sidebar' | 'fullBody';
};

const HEADLINE_TEXT =
  'Get the resources you need to become a video marketing pro.';

const Container = styled.aside`
  background-color: ${({ theme }) => theme.colors.blue700};
  border-radius: ${({ theme }) => theme.radius.sm};
  color: ${({ theme }) => theme.colors.white};
  display: flex;
  flex-direction: column;
  text-align: center;
  gap: ${({ theme }) => theme.spacing[0.75]};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[1.5]}`};
  margin-block: ${({ theme }) => theme.spacing[1]};

  ${({ theme }) => theme.mq.minWidth.medium} {
    gap: ${({ theme }) => theme.spacing[1]};
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    margin-block: ${({ theme }) => theme.spacing[2]};
    padding-inline: ${({ theme }) => theme.spacing[4]};

    .Subscribe__form {
      padding-inline: 0;
    }

    &.fullBody {
      margin-block: ${({ theme }) => theme.spacing[4]};

      .Subscribe__form {
        padding-inline: ${({ theme }) => theme.spacing[7]};
        display: flex;
        flex-direction: column;
      }
    }
  }

  // Only the bare sidebar heading needs this; every paragraph in here is a
  // styled component that sets its own margins, and a bare p rule would
  // outrank them.
  h3 {
    margin: 0;
    padding-block: 0;
  }

  /* Sidebar variant: float into the right margin on xl so article text stays full width */
  &.sidebar {
    ${sidebarCalloutFloatXl}
  }
`;

/**
 * Full body headling needs to be styled as an h1 because h2 is too
 * small for where it's placed on the page (below the article content).
 */
const FullBodyHeadline = styled.h2`
  font-size: ${blogTokens.typography.fontSize.headingOne};
  font-weight: ${blogTokens.typography.fontWeight.headingOne};
  padding-inline: ${({ theme }) => theme.spacing[2]};
  // The shared blog h2 rule adds top-only padding to space article headings
  // off the paragraph above them; inside this centered container it just
  // makes the top padding read heavier than the bottom.
  padding-block: 0;
  margin: 0;
  // Keep the balanced default; the blog-base override only targets prose body headings.
  text-wrap: balance;

  ${({ theme }) => theme.mq.minWidth.large} {
    font-size: ${blogTokens.typography.fontSize.desktop.headingOne};
    padding-inline: ${({ theme }) => theme.spacing[7]};
  }
`;

const LegalText = styled.p`
  // Custom font size (10px) per Design's request - there is no token
  font-size: 0.625rem;
  margin-block: 0;

  // Deliberately a descendant selector: these links inherit the shared blog
  // anchor styling, which only applies to anchors without a class. Making them
  // a styled component would add one and opt them out of it.
  a:not([class]) {
    color: ${({ theme }) => theme.colors.white};

    // The blog link styles are authored for the white article page, so both
    // states land too close to this module's dark blue container. Only the
    // text colors are corrected here — the hover wash, underline, and
    // spacing still come from the shared blog styles.
    &:hover,
    &:active {
      color: ${({ theme }) => theme.colors.blue800};
    }
  }
`;

/**
 * Subscribe block that invites readers to sign up for Wistia content.
 *
 * Choose the variant based on where you want the block to appear and how
 * prominent it should be:
 *
 * - **sidebar** – In the right-hand sidebar on larger viewports. This variant
 *   will shift into the article on smaller viewports. Use for a persistent but
 *   secondary offer on the post page.
 * - **fullBody** – Full-width block at the bottom of the post. Use when the
 *   subscribe CTA is a primary takeaway at the end of the article.
 */
const Subscribe = ({ variant = 'sidebar' }: SubscribeProps) => {
  return (
    <Container className={variant}>
      {variant === 'fullBody' && (
        <FullBodyHeadline>{HEADLINE_TEXT}</FullBodyHeadline>
      )}
      {variant === 'sidebar' && <h3>{HEADLINE_TEXT}</h3>}
      <BlogSubscribeForm className="Subscribe__form" />
      <LegalText>
        By clicking this button, you agree to Wistia’s{' '}
        <Link href="/terms">Terms of Service</Link> and{' '}
        <Link href="/privacy">Privacy Policy</Link>.
        {RECAPTCHA_ENABLED && ' This site is protected by reCAPTCHA.'}
      </LegalText>
    </Container>
  );
};

export default Subscribe;
